import { create } from 'zustand'
import type { FeatureStatus } from '@/lib/readiness/module'

// ── Profile and feature gates (Iteration 6 POC §1–§2, Note 1) ──────────────
// One build, two profiles: `poc` hides or locks features, `full` is the build
// as it stands. `profile` and the gate list live in one store; components ask
// `useIsLocked(id)` / `useIsHidden(id)` and never branch on `profile` itself.
//
// Resolved once on load: `?profile=` in the URL, then
// `localStorage['quantizant.profile']`, then `poc`. Writing the URL and
// storage on change is `setProfile`; there is no switch in the UI, so a
// `?profile=` link is the way to change it.

export type Profile = 'poc' | 'full'
export const PROFILE_KEY = 'quantizant.profile'
export const PROFILE_PARAM = 'profile'

export type GateId =
  | 'progress'
  | 'migration_tracker'
  | 'technology_catalogue'
  | 'save_assessment'
  | 'index_switch'
  | 'objective_detail'
  | 'portfolio_assistant'
  // Dashboard trims (§6): removed rather than locked, so `hidden`.
  | 'readiness_score_tile'
  | 'dashboard_tile_links'
  | 'view_toggle'

// `hidden` removes the item outright (no locked treatment).
export type GateStatus = FeatureStatus | 'hidden'

export interface FeatureGate {
  id: GateId
  // Status in the POC profile; in Full every gate reads as `available`.
  poc: GateStatus
}

// The single source of truth for what POC gates (§2.1 table).
export const FEATURE_GATES: FeatureGate[] = [
  { id: 'progress', poc: 'locked' },
  { id: 'migration_tracker', poc: 'locked' },
  { id: 'technology_catalogue', poc: 'locked' },
  { id: 'save_assessment', poc: 'locked' },
  { id: 'index_switch', poc: 'locked' },
  { id: 'objective_detail', poc: 'locked' },
  { id: 'portfolio_assistant', poc: 'hidden' },
  { id: 'readiness_score_tile', poc: 'hidden' },
  { id: 'dashboard_tile_links', poc: 'hidden' },
  { id: 'view_toggle', poc: 'hidden' },
]

const isProfile = (v: unknown): v is Profile => v === 'poc' || v === 'full'

function readStored(): Profile | null {
  try {
    const v = localStorage.getItem(PROFILE_KEY)
    return isProfile(v) ? v : null
  } catch {
    return null
  }
}

function readUrl(): Profile | null {
  if (typeof window === 'undefined') return null
  const v = new URLSearchParams(window.location.search).get(PROFILE_PARAM)
  return isProfile(v) ? v : null
}

export interface PendingProfileEvent {
  from: Profile
  to: Profile
  source: 'menu' | 'url'
}

function initial(): { profile: Profile; pending: PendingProfileEvent | null } {
  const fromUrl = readUrl()
  const stored = readStored()
  const profile = fromUrl ?? stored ?? 'poc'
  try {
    localStorage.setItem(PROFILE_KEY, profile)
  } catch {
    /* storage unavailable: the URL still carries it */
  }
  // A link that opens a different profile than the one stored counts as a change.
  const pending = fromUrl && stored && fromUrl !== stored ? { from: stored, to: fromUrl, source: 'url' as const } : null
  return { profile, pending }
}

interface ProfileState {
  profile: Profile
  gates: FeatureGate[]
  // An analytics event waiting for the org / index scope to be known.
  pending: PendingProfileEvent | null
  setProfile: (p: Profile) => void
  clearPending: () => void
}

const init = initial()

export const useProfileStore = create<ProfileState>()((set) => ({
  profile: init.profile,
  gates: FEATURE_GATES,
  pending: init.pending,
  setProfile: (profile) =>
    set((s) => {
      if (s.profile === profile) return s
      try {
        localStorage.setItem(PROFILE_KEY, profile)
      } catch {
        /* not remembered across reloads; the URL still carries it */
      }
      return { profile, pending: { from: s.profile, to: profile, source: 'menu' } }
    }),
  clearPending: () => set({ pending: null }),
}))

export function gateStatus(s: Pick<ProfileState, 'profile' | 'gates'>, id: GateId): GateStatus {
  if (s.profile !== 'poc') return 'available'
  return s.gates.find((g) => g.id === id)?.poc ?? 'available'
}
export const isLocked = (s: Pick<ProfileState, 'profile' | 'gates'>, id: GateId) => gateStatus(s, id) === 'locked'
export const isHidden = (s: Pick<ProfileState, 'profile' | 'gates'>, id: GateId) => gateStatus(s, id) === 'hidden'

export function useIsLocked(id: GateId | undefined): boolean {
  return useProfileStore((s) => (id ? isLocked(s, id) : false))
}
export function useIsHidden(id: GateId): boolean {
  return useProfileStore((s) => isHidden(s, id))
}
